/**
 * Email-log filter URL state
 *
 * The email-log filter controls (search, reference id, status, customer,
 * date range, and histogram time-range drill-down) persist their active
 * values in the URL hash query string so the filtered view is bookmarkable
 * and shareable.
 *
 * Works in both standalone (`#/admin/email-logs?...`) and embedded-in-
 * Customer-360 (`#/customer-360?customerId=X&tab=audit&subtab=email&...`)
 * contexts because the param names chosen here don't collide with Customer
 * 360's own hash namespace (customerId, tab, subtab, user, view).
 *
 * Param schema:
 *   q       — unified search string ($or across recipientEmail, subject,
 *             recipientName)
 *   refId   — reference id, searched inside metadata JSON (emailReferenceId)
 *   status  — exact match ("QUEUED" | "SENT" | "DELIVERED" | "FAILED" |
 *             "BOUNCED" | "COMPLAINED")
 *   customer — customerId (system-admin dropdown or legacy deep-link)
 *   from    — datetime-local string ("YYYY-MM-DDTHH:mm")
 *   to      — datetime-local string ("YYYY-MM-DDTHH:mm")
 *   tStart  — ISO timestamp pinned by histogram drill-down
 *   tEnd    — ISO timestamp pinned by histogram drill-down
 *   page    — 1-based page number (preserved from legacy pagination links)
 *   id      — modal deep-link for a specific log (separate lifecycle —
 *             managed by the page coordinator, not by this helper)
 *
 * Legacy param names still accepted on read (translated to the new schema):
 *   email=...         → q=...
 *   dateFrom=...      → from=...
 *   timeStart/timeEnd → tStart/tEnd
 */

const EMAIL_FILTER_KEYS = [
    'q',
    'refId',
    'status',
    'customer',
    'from',
    'to',
    'tStart',
    'tEnd',
    'page',
];

function splitHash(hash) {
    const h = hash || '';
    const qIdx = h.indexOf('?');
    return {
        route: qIdx === -1 ? h : h.slice(0, qIdx),
        query: qIdx === -1 ? '' : h.slice(qIdx + 1),
    };
}

/**
 * Read email-log filter state from a hash string (defaults to the current
 * window's hash). Never throws — returns empty fields for anything missing.
 */
export function readEmailFilters(hash = window.location.hash) {
    const { query } = splitHash(hash);
    const p = new URLSearchParams(query);

    return {
        q: p.get('q') || p.get('email') || '',
        refId: p.get('refId') || '',
        status: p.get('status') || '',
        customer: p.get('customer') || '',
        from: p.get('from') || p.get('dateFrom') || '',
        to: p.get('to') || '',
        tStart: p.get('tStart') || p.get('timeStart') || '',
        tEnd: p.get('tEnd') || p.get('timeEnd') || '',
        page: p.get('page') || '',
    };
}

/**
 * Custom event fired after writeEmailFilters updates the URL. history.pushState
 * / replaceState do not trigger the native `hashchange` event, so anyone
 * listening for filter updates (e.g. email-log-filters.tag) should listen to
 * both events — `hashchange` for browser back/forward, this one for in-app
 * writes.
 */
export const EMAIL_FILTERS_EVENT = 'cloudcore:email-filters-changed';

/**
 * Write email-log filter state to the URL hash, preserving any non-filter
 * params the caller (e.g. Customer 360, or the modal's `id` deep-link)
 * owns on the same hash.
 *
 * options.replace — use history.replaceState instead of pushState (default push).
 */
export function writeEmailFilters(filters, options = {}) {
    const { route, query } = splitHash(window.location.hash);
    const p = new URLSearchParams(query);

    // Strip every filter key (including legacy ones) so stale values don't
    // linger when we re-write. Non-filter params (tab, subtab, customerId,
    // user, view, id, etc.) pass through untouched.
    for (const key of [...EMAIL_FILTER_KEYS, 'email', 'dateFrom', 'timeStart', 'timeEnd']) {
        p.delete(key);
    }

    for (const key of EMAIL_FILTER_KEYS) {
        const value = filters?.[key];
        if (value !== undefined && value !== null && value !== '') {
            p.set(key, value);
        }
    }

    const qs = p.toString();
    const newHash = qs ? `${route}?${qs}` : route;
    if (newHash === window.location.hash) return;

    const method = options.replace ? 'replaceState' : 'pushState';
    window.history[method](null, '', newHash);
    window.dispatchEvent(new CustomEvent(EMAIL_FILTERS_EVENT));
}

/**
 * True if the filters object has any scoping beyond the default sort.
 */
export function hasEmailFilters(filters) {
    if (!filters) return false;
    return EMAIL_FILTER_KEYS.some((key) => {
        const value = filters[key];
        return value !== undefined && value !== null && value !== '';
    });
}

/**
 * Shape an empty filter state — useful as the reset default and for
 * comparisons. `page` is omitted here because the filter row doesn't own it.
 */
export function emptyEmailFilters() {
    return {
        q: '',
        refId: '',
        status: '',
        customer: '',
        from: '',
        to: '',
        tStart: '',
        tEnd: '',
    };
}
